import { JxsComponent } from '@jxs/component';
import { formatMoney } from '@jxs/money-format';
import { replaceNodeText, copyToClipboard } from '@jxs/utilities';

window.JxsUtils = {
  refreshCart: function() {
    const isCartPage = document.body.classList.contains('template-cart');
    const hasCartDrawer = !!document.getElementById('halo-cart-sidebar');
    if (isCartPage || hasCartDrawer) {
      Shopify.getCart((cart) => {
        if (isCartPage) {
          window.halo.updateCart(cart);
        } else if (window.halo && hasCartDrawer) {
          document.body.classList.add('cart-sidebar-show');
          window.halo.updateSidebarCart(cart);
        }
      });
    }
  }
}

/**
 * JxsSectionSettings - Section 配置桥接组件。
 *
 * 该组件通常由 `_jxs-section-settings.liquid` 输出到页面中，本身不负责展示内容，
 * 只负责读取 Liquid 渲染出的 data-* 配置，并把这些配置转换成实际作用在
 * `.jxs-section-body` / `.shopify-section` 上的 class 或 DOM 结构。
 *
 * 当前主要处理：
 * - 移动端/桌面端 section 宽度模式（全宽、页面宽度）；
 * - 将背景图元素移动到 Shopify section 根节点前方，方便统一定位和层级控制。
 */
class JxsSectionSettings extends JxsComponent {
  constructor() {
    super();
    const section = this.closest('.shopify-section');
    // 配置组件只作为数据载体存在，不参与页面视觉展示。
    this.style.display = 'none'
    const sectionBody = this.closest('.jxs-section')?.querySelector('.jxs-section-body');
    if (sectionBody) {
      // 如果 Liquid 或其他脚本已经设置过宽度 class，则不重复覆盖，避免产生冲突。
      if (sectionBody.classList.contains('jxs-section--full-width') || sectionBody.classList.contains('jxs-section--page-width')) {
        return;
      }

      // 移动端宽度：默认按页面宽度处理，data-page-width-mobile="full" 时切换为全宽。
      if (this.dataset.pageWidthMobile == 'full') {
        sectionBody.classList.add('jxs-section--full-width');
      } else if (this.dataset.pageWidthMobile == 'large') {
        sectionBody.classList.add('jxs-section--page-large-width');
      } else {
        sectionBody.classList.add('jxs-section--page-width');
      }

      // 桌面端宽度：使用独立 class，便于 CSS 在不同断点下覆盖移动端规则。
      if (this.dataset.pageWidthDesktop == 'full') {
        sectionBody.classList.add('jxs-section--desktop-full-width');
      } else if (this.dataset.pageWidthDesktop == 'large') {
        sectionBody.classList.add('jxs-section--desktop-page-large-width');
      } else {
        sectionBody.classList.add('jxs-section--desktop-page-width');
      }
    }

    const backgroundImage = this.querySelector('.jxs-section-background-image')
    if (backgroundImage) {
      if (section) {
        // 背景图脱离配置组件，提升到 section 根节点，避免被内容容器布局影响。
        section.prepend(backgroundImage);
      }
    }

    if (!this.getDataset('maxWidthDisabled')) {
      section?.classList.add('jxs-section--max-width-enabled');
    }
  }
}
customElements.define('jxs-section-settings', JxsSectionSettings);

/**
 * JxsMultiSpanText - 多段文本样式标记组件。
 *
 * 用于把富文本中的简写标记 `[序号 文本]` 转换成可单独设置样式的 span。
 * 搭配内部由 Liquid 输出的 `style[sn][block-id]`，可以让不同序号的文字片段
 * 绑定到对应 block 的样式 class，从而实现同一段标题/文案中局部变色、加粗等效果。
 *
 * 示例：
 * 输入：`新品 [1 限时折扣] 今日开启`
 * 输出：`新品 <span class="text-span" sn="1">限时折扣</span> 今日开启`
 */
class JxsMultiSpanText extends JxsComponent {
  constructor() {
    super();
  }

  /**
   * 元素挂载后解析文本标记并应用对应样式。
   *
   * 解析和样式绑定放在 connectedCallback 中执行，是为了确保组件内部的
   * 文本节点、style 标签和 Liquid 输出内容都已经进入 DOM。
   */
  connectedCallback() {
    super.connectedCallback()
    this.parseSpanMarkers();
    this.applySpanStyles();
  }

  /**
   * 解析 [n 文字] 格式，转换为 <span class="text-span" sn="n">文字</span>
   */
  parseSpanMarkers() {
    const pattern = /\[(\d+)\s+([^\]]+)\]/g;
    this.innerHTML = this.innerHTML.replace(pattern, '<span class="text-span" sn="$1">$2</span>');
  }

  /**
   * 找到所有 text-span 子块的 style，并为对应 sn 的 span 设置 id
   * 使 #block-id 选择器能匹配上
   */
  applySpanStyles() {
    const spanStyles = this.querySelectorAll('style[sn][block-id]');
    
    spanStyles.forEach(styleEl => {
      const sn = styleEl.getAttribute('sn');
      const blockId = styleEl.getAttribute('block-id');
      
      if (sn && blockId) {
        this.querySelectorAll(`.text-span[sn="${sn}"]`).forEach(targetSpan => {
          if (targetSpan && !targetSpan.classList.contains(blockId)) {
            targetSpan.classList.add(blockId)
          }
        })
      }
    });
  }
}
customElements.define('jxs-multi-span-text-component', JxsMultiSpanText);


/*
  功能：当容器内的元素获得 active 属性时，自动滚动使该元素居中显示

  使用方式：
  // 这些item如果被设置了active属性，那么这个item元素会被自动滚动到容器的中间展示。
  // 注意，一次只能有一个item为active。
  {% capture items %}
    <div>1</div>
    <div>2</div>
    <div>3</div>
    <div>4</div>
  {% endcapture %}
  // 如果需要，给一个容器来限制宽度
  <div class="container">
    {% render 'jxs-auto-scroll-layout', items: items %}
  </div>
  <style>
    .container {
      overflow: hidden;
    }
  </style>


  特性：
  - 水平滚动，隐藏滚动条（需配合CSS）
  - 监听子元素 active 属性变化
  - 平滑滚动到激活元素并居中显示
*/
class JxsAutoScrollLayout extends JxsComponent {
  constructor() {
    super();
    // MutationObserver 实例，用于监听属性变化
    this.observer = null;

    // 拖拽滚动状态
    this.isDragging = false;
    this.dragStartX = 0;
    this.dragStartScrollLeft = 0;
    this.dragPointerId = null;
    this.hasDragged = false;
    this.shouldPreventClick = false;
    this.dragThreshold = 5;
    this.pendingScrollToActive = false;

    this.handlePointerDown = this.handlePointerDown.bind(this);
    this.handlePointerMove = this.handlePointerMove.bind(this);
    this.handlePointerUp = this.handlePointerUp.bind(this);
    this.handleClick = this.handleClick.bind(this);
    this.handleDragStart = this.handleDragStart.bind(this);
  }

  /**
   * 元素插入DOM时调用
   * 初始化监听器并立即滚动到当前激活的元素
   */
  connectedCallback() {
    super.connectedCallback();
    if (this.getDataset('autoScroll')) {
      this.initObserver();
      this.scrollToActive();
    }
    this.initDragScroll();
  }

  /**
   * 元素从DOM移除时调用
   * 清理 MutationObserver，防止内存泄漏
   */
  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.observer) {
      this.observer.disconnect();
    }
    this.destroyDragScroll();
  }

  /**
   * 初始化 MutationObserver
   * 只监听 wrapper 直接子元素的 active 属性变化
   */
  initObserver() {
    if (this.observer) {
      this.observer.disconnect();
    }

    const wrapper = this.refs.wrapper;
    if (!(wrapper instanceof Element)) {
      console.warn('wrapper ref is missing');
      return;
    }

    this.observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        // 当被观察元素的 active 属性发生变化时触发滚动
        if (mutation.type === 'attributes' && mutation.attributeName === 'active') {
          this.scrollToActive();
        }
      });
    });

    const observer = this.observer;

    Array.from(wrapper.children).forEach((element) => {
      observer.observe(element, {
        attributes: true,
        attributeFilter: ['active']
      });
    });
  }

  /**
   * 初始化拖拽滚动
   * 使用 Pointer Events 同时支持鼠标拖拽和触摸拖拽
   */
  initDragScroll() {
    this.addEventListener('pointerdown', this.handlePointerDown);
    window.addEventListener('click', this.handleClick, true);
    this.addEventListener('dragstart', this.handleDragStart);
  }

  /**
   * 销毁拖拽滚动监听器
   */
  destroyDragScroll() {
    this.removeEventListener('pointerdown', this.handlePointerDown);
    window.removeEventListener('pointermove', this.handlePointerMove);
    window.removeEventListener('pointerup', this.handlePointerUp);
    window.removeEventListener('pointercancel', this.handlePointerUp);
    window.removeEventListener('click', this.handleClick, true);
    this.removeEventListener('dragstart', this.handleDragStart);
  }

  /**
   * @param {PointerEvent} event
   */
  handlePointerDown(event) {
    // 鼠标只响应左键；触摸/触控笔无 button 时正常响应
    if (event.button !== undefined && event.button !== 0) {
      return;
    }

    // 没有可滚动内容时不进入拖拽状态，避免影响普通点击
    if (this.scrollWidth <= this.clientWidth) {
      return;
    }

    this.isDragging = true;
    this.hasDragged = false;
    this.shouldPreventClick = false;
    this.dragPointerId = event.pointerId;
    this.dragStartX = event.clientX;
    this.dragStartScrollLeft = this.scrollLeft;
    this.classList.add('is-dragging');

    // 不在 pointerdown 时捕获 pointer，避免普通点击被重定向到容器，导致子元素 click 失效。
    window.addEventListener('pointermove', this.handlePointerMove, { passive: false });
    window.addEventListener('pointerup', this.handlePointerUp);
    window.addEventListener('pointercancel', this.handlePointerUp);
  }

  /**
   * @param {PointerEvent} event
   */
  handlePointerMove(event) {
    if (!this.isDragging || event.pointerId !== this.dragPointerId) {
      return;
    }

    const deltaX = event.clientX - this.dragStartX;

    if (Math.abs(deltaX) > this.dragThreshold) {
      this.hasDragged = true;
      this.shouldPreventClick = true;
    }

    if (this.hasDragged) {
      event.preventDefault();
      this.scrollLeft = this.dragStartScrollLeft - deltaX;
    }
  }

  /**
   * @param {PointerEvent} event
   */
  handlePointerUp(event) {
    if (!this.isDragging || event.pointerId !== this.dragPointerId) {
      return;
    }

    window.removeEventListener('pointermove', this.handlePointerMove);
    window.removeEventListener('pointerup', this.handlePointerUp);
    window.removeEventListener('pointercancel', this.handlePointerUp);

    this.isDragging = false;
    this.dragPointerId = null;
    this.classList.remove('is-dragging');

    if (this.hasDragged) {
      window.setTimeout(() => {
        this.shouldPreventClick = false;
      }, 120);
    }

    this.hasDragged = false;

    if (this.pendingScrollToActive) {
      this.pendingScrollToActive = false;
      this.scrollToActive();
    }
  }

  /**
   * 拖拽超过阈值后阻止 click，避免拖动标签栏时误触 tab 切换
   * @param {MouseEvent} event
   */
  handleClick(event) {
    const path = event.composedPath?.() || [];

    if (!this.shouldPreventClick || !path.includes(this)) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    this.shouldPreventClick = false;
  }

  /**
   * 阻止浏览器原生图片/链接拖拽，保证横向拖拽滚动稳定
   * @param {DragEvent} event
   */
  handleDragStart(event) {
    if (this.isDragging) {
      event.preventDefault();
    }
  }

  /**
   * 滚动到激活的元素并居中
   * 计算目标滚动位置，使激活元素在可视区域中间显示
   */
  scrollToActive() {
    if (this.isDragging) {
      this.pendingScrollToActive = true;
      return;
    }

    const wrapper = this.refs.wrapper;
    if (!(wrapper instanceof Element)) {
      return;
    }

    // 查找当前激活的 wrapper 直接子元素
    const activeElement = Array.from(wrapper.children).find((element) => element.hasAttribute('active'));
    if (activeElement) {
      const containerRect = this.getBoundingClientRect();
      const elementRect = activeElement.getBoundingClientRect();
      
      // 计算目标滚动位置：当前滚动位置 + 元素相对容器的偏移量 - 容器宽度的一半 + 元素宽度的一半
      // 这样可以让激活元素正好居中显示
      const scrollLeft = this.scrollLeft + (elementRect.left - containerRect.left) - (containerRect.width / 2) + (elementRect.width / 2);
      
      // 平滑滚动到目标位置
      this.scrollTo({
        left: scrollLeft,
        behavior: 'smooth'
      });
    }
  }
}
customElements.define('jxs-auto-scroll-layout', JxsAutoScrollLayout);

/**
 * JxsVideoSimulation - 轻量级“类 video”播放状态模拟组件。
 *
 * 该组件不渲染真实视频，只模拟 HTMLVideoElement 常用的播放属性和事件：
 * - currentTime / duration / paused / ended / playbackRate；
 * - play() / pause()；
 * - timeupdate / pause / ended 事件。
 *
 * 典型用途是在没有真实视频资源、但仍需要驱动进度条或轮播播放状态时，
 * 提供一个与 video 接近的状态源，便于复用依赖 video-like 对象的组件逻辑。
 */
class JxsVideoSimulation extends HTMLElement {
  constructor() {
    super();
    // 当前播放时间，内部统一使用毫秒存储；对外 getter/setter 使用秒，保持和 video API 一致。
    this._currentTime = 0;
    // 模拟总时长，单位毫秒；可通过 data-duration 配置，默认 5000ms。
    this._duration = parseInt(this.dataset.duration || '5000');
    // 播放速率，保留 video-like API 形态，当前默认固定为 1。
    this._playbackRate = 1;
    // 播放状态标记，true 表示正在通过 requestAnimationFrame 推进时间。
    this._isPlaying = false;
    // 结束状态标记，用于对外暴露 ended，并控制再次 play 时是否从头开始。
    this._ended = false;
    // 当前 requestAnimationFrame id，用于暂停或重启时取消旧帧。
    this._animationId = null;
    // 上一帧时间戳，用于计算两帧之间经过的时间。
    this._lastTime = 0;
    // 播放令牌，用于让旧的 rAF 回调失效，避免暂停/重播后旧回调继续推进。
    this._playToken = 0;
  }

  /**
   * 当前播放时间，单位秒。
   *
   * 对外保持 HTMLVideoElement.currentTime 的语义，内部再转换为毫秒计算。
   */
  get currentTime() {
    return this._currentTime / 1000;
  }

  /**
   * 手动设置当前播放时间，单位秒。
   *
   * 常用于外部 seek 或初始化进度；设置后会同步 ended 状态，并重置上一帧时间戳，
   * 避免下一次动画帧把 seek 前后的时间差错误叠加到播放进度中。
   */
  set currentTime(value) {
    const nextTime = Number(value) * 1000;

    this._currentTime = Number.isFinite(nextTime) ? Math.max(nextTime, 0) : 0;
    this._ended = this._duration > 0 && this._currentTime >= this._duration;
    // 重置时间戳避免计算跳跃
    this._lastTime = performance.now();
  }

  /**
   * 模拟总时长，单位秒。
   */
  get duration() {
    return this._duration / 1000;
  }

  /**
   * @param {number} value 单位毫秒
   */
  set duration(value) {
    const nextDuration = Number(value);

    this._duration = Number.isFinite(nextDuration) && nextDuration > 0 ? nextDuration : 0;

    if (this._duration > 0 && this._currentTime > this._duration) {
      this._currentTime = this._duration;
    }

    this._ended = this._duration > 0 && this._currentTime >= this._duration;
  }

  /**
   * 是否处于暂停状态。
   *
   * 与 HTMLVideoElement.paused 对齐：没有播放时返回 true。
   */
  get paused() {
    return !this._isPlaying;
  }

  /**
   * 是否已经播放到末尾。
   */
  get ended() {
    return this._ended;
  }

  /**
   * 当前播放倍率。
   */
  get playbackRate() {
    return this._playbackRate;
  }

  /**
   * 元素插入页面时执行。
   *
   * 自定义元素默认 display 可能是 inline，这里修正为 block，方便外部脚本绑定事件
   * 或在调试时观察元素占位。
   */
  connectedCallback() {
    // 确保元素可以接收事件监听
    if (!this.style.display || this.style.display === 'inline') {
      this.style.display = 'block';
    }
  }

  /**
   * 元素移除时暂停模拟播放，清理动画帧。
   */
  disconnectedCallback() {
    this.pause();
  }

  /**
   * 开始或恢复模拟播放。
   *
   * 如果已经结束，则从 0 重新开始；否则从当前 currentTime 继续。
   */
  play() {
    if (this._isPlaying) return;

    if (this._ended || (this._duration > 0 && this._currentTime >= this._duration)) {
      this._currentTime = 0;
      this._ended = false;
    }

    this._isPlaying = true;
    this._lastTime = performance.now();
    this._playToken += 1;
    this._queueAnimationFrame(this._playToken);
  }

  /**
   * 暂停模拟播放。
   *
   * 停止 rAF 推进并派发 pause/timeupdate，让依赖播放状态的组件可以立即同步 UI。
   */
  pause() {
    const wasPlaying = this._isPlaying;

    this._isPlaying = false;
    this._playToken += 1;

    if (this._animationId) {
      cancelAnimationFrame(this._animationId);
      this._animationId = null;
    }

    if (!wasPlaying) return;

    this.dispatchEvent(new CustomEvent('pause', {
      bubbles: true,
      cancelable: false
    }));

    // 立即同步一次 paused 状态，让依赖 timeupdate 的调用方停止补帧。
    this.dispatchEvent(new CustomEvent('timeupdate', {
      bubbles: true,
      cancelable: false
    }));
  }

  /**
   * 安排下一帧动画。
   *
   * @param {number} token 当前播放令牌，用于过滤过期回调。
   */
  _queueAnimationFrame(token) {
    if (this._animationId) {
      cancelAnimationFrame(this._animationId);
    }

    this._animationId = requestAnimationFrame(() => this._animate(token));
  }

  /**
   * rAF 主循环：根据上一帧到当前帧的时间差推进模拟播放时间。
   *
   * @param {number} token 当前播放令牌，用于确保只有最新一轮播放能更新状态。
   */
  _animate(token) {
    this._animationId = null;

    if (!this._isPlaying || token !== this._playToken) return;

    const now = performance.now();
    const delta = now - this._lastTime;
    this._lastTime = now;

    this._currentTime += delta * this._playbackRate;

    // 模拟结束
    if (this._duration > 0 && this._currentTime >= this._duration) {
      this._currentTime = this._duration;
      this._ended = true;
      this._isPlaying = false;
      this._playToken += 1;

      // 先发送最终 timeupdate，让进度条可以按 ended 状态渲染到 100%。
      this.dispatchEvent(new CustomEvent('timeupdate', {
        bubbles: true,
        cancelable: false
      }));

      this.dispatchEvent(new CustomEvent('ended', {
        bubbles: true,
        cancelable: false
      }));

      return;
    }

    // 发送 timeupdate 事件，模拟 video 的行为
    this.dispatchEvent(new CustomEvent('timeupdate', {
      bubbles: true,
      cancelable: false
    }));

    if (!this._isPlaying || token !== this._playToken) return;

    this._queueAnimationFrame(token);
  }
}
customElements.define('jxs-video-simulation', JxsVideoSimulation);


/**
 * 进度条默认 seek 判定阈值，单位秒。
 * 当前显示时间与新采样时间差距超过该值时，认为发生了跳转并立即吸附。
 */
const JXS_PROGRESS_BAR_SEEK_THRESHOLD = 1.5;
/**
 * 无新采样时允许继续补帧的最长时间，单位毫秒。
 * 超时后停止估算，避免进度条在外部播放源卡住或暂停时继续前进。
 */
const JXS_PROGRESS_BAR_STALL_TIMEOUT = 1200;
/**
 * 时间比较容差，单位秒。
 * 用于判断轻微误差和真实回退，避免浮点误差触发错误的 seek 判断。
 */
const JXS_PROGRESS_BAR_EPSILON = 0.02;

/**
 * JxsProgressBar - 基于外部采样数据的平滑进度条组件。
 *
 * 组件本身不绑定 video，也不主动监听 timeupdate；它只暴露 update 方法，
 * 由调用者在 timeupdate / ended / seek 等时机把当前播放状态传进来。
 *
 * 支持的调用方式：
 * - progressBar.update(videoLike)
 *   videoLike 只需要包含 currentTime、duration，可选 playbackRate、paused、ended。
 *   这个对象可以是真实 HTMLVideoElement，也可以是模拟播放器状态对象。
 *
 * - progressBar.update(percent)
 *   number 参数固定按百分比处理，范围为 0-100，适合 ended 时直接传入 100。
 *
 * 平滑策略：
 * - update(videoLike) 只记录最近一次 timeupdate 的时间采样点；
 * - 两次采样之间使用 requestAnimationFrame 根据 elapsed + playbackRate 补帧；
 * - 长时间收不到新采样时自动停止补帧，避免外部已暂停但进度条继续前进；
 * - 检测明显跳变或回退时立即吸附，保证 seek / loop 场景不会抖动。
 */
class JxsProgressBar extends JxsComponent {
  constructor() {
    super();

    // 最近一次外部传入的播放源状态。
    this._currentTime = 0;
    this._duration = 0;
    this._playbackRate = 1;
    this._paused = true;
    this._ended = false;

    // 采样与渲染状态：采样值用于估算，displayed* 用于记录当前实际显示值。
    this._sampleTime = 0;
    this._sampledAt = 0;
    this._displayedTime = 0;
    this._displayedPercent = 0;
    this._animationFrame = null;
    this._hasTimeSample = false;

    // 绑定方法上下文，确保作为回调传入 rAF 或外部事件时 this 仍指向组件实例。
    this.update = this.update.bind(this);
    this.render = this.render.bind(this);
  }

  /**
   * 元素挂载后初始化无障碍属性，并按当前缓存进度渲染一次。
   */
  connectedCallback() {
    super.connectedCallback();

    if (!this.hasAttribute('role')) {
      this.setAttribute('role', 'progressbar');
    }

    if (!this.hasAttribute('aria-valuemin')) {
      this.setAttribute('aria-valuemin', '0');
    }

    if (!this.hasAttribute('aria-valuemax')) {
      this.setAttribute('aria-valuemax', '100');
    }

    this.renderPercent(this._displayedPercent);
  }

  /**
   * 元素卸载时停止补帧，避免 rAF 回调持有已移除节点造成内存泄漏。
   */
  disconnectedCallback() {
    super.disconnectedCallback();
    this.stopAnimation();
  }

  /**
   * seek 判定阈值，单位为秒。
   * 当新的采样时间和当前显示时间差距超过该阈值时，认为发生了 seek，
   * 进度条会立即吸附到新位置，而不是继续做平滑补帧。
   */
  get seekThreshold() {
    return JxsProgressBar.getFiniteNumber(this.dataset.seekThreshold, JXS_PROGRESS_BAR_SEEK_THRESHOLD);
  }

  /**
   * 无新 timeupdate 采样时的最大补帧时间，单位为毫秒。
   * 超过该时间仍没有收到新的 update(videoLike)，组件会停止 rAF，
   * 避免外部播放源已经暂停或卡住时进度条继续向前跑。
   */
  get stallTimeout() {
    return JxsProgressBar.getFiniteNumber(this.dataset.stallTimeout, JXS_PROGRESS_BAR_STALL_TIMEOUT);
  }

  /**
   * 对外暴露的进度同步入口。
   *
   * 调用者不需要把 video 绑定给组件，只需要在 timeupdate / pause / seek / ended 等事件中调用本方法，
   * 把当前播放状态传进来。组件会把这次输入当作一个“采样点”，并在两次采样之间使用 rAF 补帧。
   *
   * 支持两种输入：
   *
   * 1. number：百分比，范围 0-100。
   *    - update(0) 表示 0%；
   *    - update(100) 表示完成，常用于 ended；
   *    - 数字不会被当作秒数处理，避免 update(30) 同时可能代表 30 秒或 30%。
   *
   * 2. video-like 对象：真实 HTMLVideoElement 或结构相同的状态对象。
   *    - currentTime：当前播放时间，单位秒；
   *    - duration：总时长，单位秒；
   *    - playbackRate：播放倍率，可选，默认 1；
   *    - paused：是否暂停，可选，缺省按播放中处理；
   *    - ended：是否结束，可选，为 true 时直接渲染到 100%。
   *
   * @param {number | { currentTime?: number, duration?: number, playbackRate?: number, paused?: boolean, ended?: boolean, percent?: number }} input 百分比或 video-like 播放状态对象。
   */
  update(input) {
    if (typeof input === 'number') {
      this.updatePercent(input);
      return;
    }

    if (!input || typeof input !== 'object') return;

    this.updateBySource(input);
  }

  /**
   * 直接按百分比更新进度。
   *
   * number 参数固定表示百分比而不是秒数，避免 update(30) 同时可能代表
   * 30 秒或 30% 的歧义。
   *
   * @param {number} percent
   */
  updatePercent(percent) {
    const nextPercent = JxsProgressBar.clamp(Number(percent), 0, 100);

    this._displayedPercent = nextPercent;
    this._displayedTime = this._duration > 0 ? (this._duration * nextPercent) / 100 : 0;
    this._paused = true;
    this._ended = nextPercent >= 100;
    this._hasTimeSample = false;

    this.stopAnimation();
    this.renderPercent(nextPercent);
  }

  /**
   * 根据 video-like 对象记录一次播放进度采样。
   *
   * @param {{ currentTime?: number, duration?: number, playbackRate?: number, paused?: boolean, ended?: boolean, percent?: number }} source
   */
  updateBySource(source) {
    const currentTime = JxsProgressBar.getFiniteNumber(source.currentTime);
    const duration = JxsProgressBar.getFiniteNumber(source.duration);

    if (!Number.isFinite(currentTime) || !Number.isFinite(duration) || duration <= 0) {
      if (Number.isFinite(Number(source.percent))) {
        this.updatePercent(Number(source.percent));
      }

      return;
    }

    const now = this.getNow();
    const playbackRate = JxsProgressBar.getFiniteNumber(source.playbackRate, 1);
    const paused = source.paused === undefined ? false : Boolean(source.paused);
    const ended = Boolean(source.ended);
    const isFirstSample = !this._hasTimeSample;
    const isBackwardSeek = !isFirstSample && currentTime + JXS_PROGRESS_BAR_EPSILON < this._currentTime;
    const isLargeJump = !isFirstSample && Math.abs(currentTime - this._displayedTime) > this.seekThreshold;
    const shouldSnap = isFirstSample || isBackwardSeek || isLargeJump || paused || ended;

    this._currentTime = currentTime;
    this._duration = duration;
    this._playbackRate = playbackRate;
    this._paused = paused || ended;
    this._ended = ended;
    this._sampleTime = currentTime;
    this._sampledAt = now;
    this._hasTimeSample = true;

    if (ended) {
      this._displayedTime = duration;
      this.stopAnimation();
      this.updateProgressUI();
      return;
    }

    if (shouldSnap) {
      this._displayedTime = JxsProgressBar.clamp(currentTime, 0, duration);
    } else if (currentTime > this._displayedTime) {
      this._displayedTime = currentTime;
    }

    this.updateProgressUI();

    if (this._paused) {
      this.stopAnimation();
      return;
    }

    this.startAnimation();
  }

  /**
   * 启动基于 requestAnimationFrame 的补帧渲染。
   */
  startAnimation() {
    if (this._animationFrame) return;

    this._animationFrame = window.requestAnimationFrame(this.render);
  }

  /**
   * 停止补帧渲染。
   */
  stopAnimation() {
    if (!this._animationFrame) return;

    window.cancelAnimationFrame(this._animationFrame);
    this._animationFrame = null;
  }

  /**
   * 在两次 timeupdate 采样之间估算当前进度，并更新 UI。
   */
  render() {
    this._animationFrame = null;

    if (!this._hasTimeSample || this._paused || this._ended || this._duration <= 0) {
      this.updateProgressUI();
      return;
    }

    const elapsedMs = this.getNow() - this._sampledAt;
    const shouldEstimate = elapsedMs <= this.stallTimeout;
    let estimatedTime = this._sampleTime;

    if (shouldEstimate) {
      estimatedTime = this._sampleTime + (elapsedMs / 1000) * this._playbackRate;
    }

    estimatedTime = JxsProgressBar.clamp(estimatedTime, 0, this._duration);

    if (this._playbackRate >= 0) {
      this._displayedTime = Math.max(this._displayedTime, estimatedTime);
    } else {
      this._displayedTime = estimatedTime;
    }

    this.updateProgressUI();

    if (shouldEstimate && this._displayedTime < this._duration) {
      this._animationFrame = window.requestAnimationFrame(this.render);
    }
  }

  /**
   * 根据当前显示时间计算百分比并写入 UI。
   */
  updateProgressUI() {
    if (this._duration <= 0) {
      this.renderPercent(this._displayedPercent);
      return;
    }

    const percent = JxsProgressBar.clamp((this._displayedTime / this._duration) * 100, 0, 100);

    this.renderPercent(percent);
  }

  /**
   * 将百分比写入 CSS 变量和无障碍属性。
   *
   * @param {number} percent
   */
  renderPercent(percent) {
    const nextPercent = JxsProgressBar.clamp(Number(percent), 0, 100);
    const progressScale = nextPercent / 100;

    this._displayedPercent = nextPercent;
    this.style.setProperty('--jxs-progress-bar-percent', `${nextPercent}%`);
    this.style.setProperty('--jxs-progress-bar-scale', String(progressScale));
    this.setAttribute('aria-valuenow', String(Math.round(nextPercent)));
    this.toggleAttribute('progress-empty', nextPercent <= 0);
    this.toggleAttribute('progress-complete', nextPercent >= 100);
  }

  /**
   * 获取高精度时间戳。
   */
  getNow() {
    return window.performance?.now ? window.performance.now() : Date.now();
  }

  /**
   * @param {unknown} value
   * @param {number} [fallback]
   */
  static getFiniteNumber(value, fallback = NaN) {
    const number = Number(value);

    return Number.isFinite(number) ? number : fallback;
  }

  /**
   * @param {number} value
   * @param {number} min
   * @param {number} max
   */
  static clamp(value, min, max) {
    if (Number.isNaN(value)) return min;

    return Math.min(Math.max(value, min), max);
  }
}
customElements.define('jxs-progress-bar', JxsProgressBar);


/*
  展示产品的折后价。
  展示：
  利用frontstore api创建一个临时的购物车对象，将折扣码和产品变体放进去，
  拿到折扣码的折后价，然后展示出来。

  复制：
  点击复制折扣码，可将折扣码复制到剪贴板，并且将折扣码应用到主购物车中。
*/
class JxsProductDiscount extends JxsComponent {
  #variantSelect = null;

  constructor() {
    super();
    this.discountCode = this.getDataset('code')
  }

  connectedCallback() {
    super.connectedCallback();
    this.showDiscountPrice(this.getDataset('variantId'));
    this.#variantSelect = this.parentElement.querySelector('variant-radios, variant-selects');
    this.#variantSelect?.addEventListener('variantChanged', this.#handleVariantChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#variantSelect?.removeEventListener('variantChanged', this.#handleVariantChange);
  }

  #handleVariantChange = e => {
    const vid = e.detail.variant.id;
    this.showDiscountPrice(vid);
  }

  async showDiscountPrice(variantId) {
    const code = this.discountCode;
    const shopDomain = this.getDataset('shopDomain') || window.Shopify.shop;

    // 基础校验
    if (!variantId || !code) {
      console.warn('[JxsProductDiscount] 缺少 variantId 或 discount code');
      return;
    }

    try {
      const cart = await this.createCartWithDiscount(variantId, code, shopDomain);
      if (!cart.discountCodes[0].applicable) {
        throw new Error('折扣应用失败，可能和其他折扣互斥，请检查')
      }
      const amount = parseFloat(cart.lines.edges[0].node.merchandise.price.amount);
      const discountAmount = parseFloat(cart.lines.edges[0].node.discountAllocations[0].discountedAmount.amount);
      const discountPrice = amount - discountAmount;
      const textNode = document.importNode(this.refs.tmpl.content, true);
      const replacements = [
        ['_price_', formatMoney(discountPrice * 100)], 
        ['_discount_', formatMoney(discountAmount * 100)], 
        ['_code_', code]
      ];
      replaceNodeText(textNode, replacements)
      this.refs.info.innerHTML = '';
      this.refs.info.append(textNode);
      this.setAttribute('enabled', '');
    } catch (err) {
      if (Shopify.designMode) {
        this.setAttribute('enabled', '');
      } else {
        this.removeAttribute('enabled');
      }
      this.refs.info.innerHTML = '此信息只在模板编辑模式下可看：' + err.message;
      console.error('[JxsProductDiscount] 调用 Storefront API 失败:', err);
    }
  }

  /**
   * 调用 Storefront API 创建带折扣的购物车
   */
  async createCartWithDiscount(variantId, discountCode, shopDomain) {
    variantId = 'gid://shopify/ProductVariant/' + variantId;
    const endpoint = `https://${shopDomain}/api/2026-01/graphql.json`;

    const query = `
      mutation cartCreate($input: CartInput!) {
        cartCreate(input: $input) {
          cart {
            lines(first: 1) {
              edges {
                node {
                  merchandise {
                    ... on ProductVariant {
                      title
                      price {
                        amount
                        currencyCode
                      }
                    }
                  }
                  discountAllocations {
                    discountedAmount {
                      amount
                      currencyCode
                    }
                  }
                }
              }
            }
            discountCodes {
              code
              applicable
            }
          }
          userErrors {
            field
            message
          }
        }
      }
    `;

    const variables = {
      input: {
        lines: [
          {
            merchandiseId: variantId,
            quantity: 1
          }
        ],
        discountCodes: [discountCode]
      }
    };

    const res = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ query, variables })
    });

    const data = await res.json();

    // 处理 userErrors
    if (data.data?.cartCreate?.userErrors?.length) {
      throw new Error(data.data.cartCreate.userErrors.map(e => e.message).join(', '));
    }

    if (!data.data?.cartCreate?.cart) {
      throw new Error('创建购物车失败');
    }

    return data.data.cartCreate.cart;
  }

  async copyCode(data) {
    if (data.apply) {
      this.refs.button.setAttribute('disabled', '');
      this.refs.button.setAttribute('loading', '');
      try {
        await this.applyDiscountCode();
      } catch (e) {
      }
      this.refs.button.removeAttribute('disabled');
      this.refs.button.removeAttribute('loading');
    }
    const copySuccess = await copyToClipboard(this.discountCode);
    if (copySuccess) {
      this.refs.buttonText.textContent = this.refs.button.dataset.buttonTextCopied;
      clearTimeout(this._clearButtonTextId);
      this._clearButtonTextId = setTimeout(() => {
        this.refs.buttonText.textContent = this.refs.button.dataset.buttonText;
      }, 2000)
    }
  }

  async applyDiscountCode() {
    const cartCodes = await this.getCartDiscountCodes();
    if (cartCodes.includes(this.discountCode)) {
      return
    }
    cartCodes.push(this.discountCode);
    const response = await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        discount: cartCodes.join(',')
      })
    });
    Shopify.getCart((cart) => {
      window.halo?.updateSidebarCart(cart);
    });
  }

  async getCartDiscountCodes() {
    const response = await fetch('/cart.js', {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' }
    });
    const cart = await response.json();
    const discountCodes = cart.discount_codes.map(item => item.code);
    return discountCodes;
  }
}

customElements.define('jxs-product-discount', JxsProductDiscount);


class JxsQuantityInput extends JxsComponent {
  constructor() {
    super();
  }

  connectedCallback() {
    super.connectedCallback();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
  }

  updateQuantity(data) {
    console.log('===', data)
    let q = 1
    try {
      q = parseInt(this.refs.quantity.value);
    } catch (e) {}
    if (isNaN(q)) {
      q = 1;
    }
    q = q + data.val;
    if (q <= 0) {
      q = 1;
    }
    this.refs.quantity.value = q;
  }

  getQuantity() {
    return parseInt(this.refs.quantity.value) || 1;
  }
}
customElements.define('jxs-quantity-input', JxsQuantityInput);

class JxsAlsoBuyProduct extends JxsComponent {
  constructor() {
    super();
  }

  connectedCallback() {
    super.connectedCallback();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
  }

  handleAddToCart() {
    this.refs.button.classList.add('is-loading');
    const quantity = this.refs.quantity_input.getQuantity();
    const variantId = this.refs.variant.selectedOptions[0]?.value;
    Shopify.addItem(variantId, quantity, this.refs.button, () => {
      this.refs.button.classList.remove('is-loading');
      if (document.getElementById('halo-cart-sidebar')) {
        Shopify.getCart((cart) => {
          document.body.classList.add('cart-sidebar-show');
          if (window.halo && typeof halo.updateSidebarCart === 'function') {
            window.halo.updateSidebarCart(cart);
          }
        });
      }
    });
  }
}
customElements.define('jxs-also-buy-product', JxsAlsoBuyProduct);


class JxsProductKeyFeatures extends JxsComponent {
  constructor() {
    super();
  }

  connectedCallback() {
    super.connectedCallback();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
  }

  toggleAll() {
    if (this.dataset.show == 'all') {
      this.dataset.show = 'less';
      this.refs.button.querySelector('span').textContent = this.refs.button.dataset.showAll;
    } else {
      this.dataset.show = 'all';
      this.refs.button.querySelector('span').textContent = this.refs.button.dataset.showLess;
    }
  }
}
customElements.define('jxs-product-key-features', JxsProductKeyFeatures);


class JxsCartDrawerPromotionProducts extends JxsComponent {

  #swiper = null;

  constructor() {
    super();
  }

  connectedCallback() {
    super.connectedCallback();
    this.#initSwiper();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#swiper?.destroy(true, true);
  }

  #initSwiper() {
    if (!this.refs.swiper?.querySelector('.swiper-slide')) return;
    this.#swiper = new Swiper(this.refs.swiper, {
      loop: false,
      autoplay: false,
      rewind: false,
      slidesPerView: 1.25,
      direction: 'horizontal',
      spaceBetween: 14,
      breakpoints: {
        768: {
          direction: 'vertical',
          slidesPerView: 'auto'
        }
      }
    });
  }
}
customElements.define('jxs-cart-drawer-promotion-products', JxsCartDrawerPromotionProducts);


class JxsQuickAddVariants extends JxsComponent {
  constructor() {
    super();
  }

  connectedCallback() {
    super.connectedCallback();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
  }

  addToCart() {
    if (this.refs.addButton.classList.contains('is-loading')) {
      return;
    }

    const variantId = this.querySelector('input[type="radio"]:checked')?.value;
    const quantityComponent = document.getElementById(this.getAttribute('quantity-id'));
    const quantity = quantityComponent?.getQuantity() || 1;
    this.refs.addButton.classList.add('is-loading');
    Shopify.addItem(variantId, quantity, this.refs.addButton, () => {
      this.refs.addButton.classList.remove('is-loading');
      this.closest('jxs-modal')?.closeModal();
      window.JxsUtils.refreshCart();
    });
  }
}
customElements.define('jxs-quick-add-variants', JxsQuickAddVariants);

class JxsProductQuickAdd extends JxsComponent {
  constructor() {
    super();
  }

  connectedCallback() {
    super.connectedCallback();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
  }

  addToCart() {
    if (this.refs.addButton.classList.contains('is-loading')) {
      return;
    }

    const variantId = this.dataset.variantId;
    const quantityComponent = document.getElementById(this.getAttribute('quantity-id'));
    const quantity = quantityComponent?.getQuantity() || 1;
    this.refs.addButton.classList.add('is-loading');
    Shopify.addItem(variantId, quantity, this.refs.addButton, () => {
      this.refs.addButton.classList.remove('is-loading');
      window.JxsUtils.refreshCart();
    });
  }
}
customElements.define('jxs-product-quick-add', JxsProductQuickAdd);

/**
 * JxsArticleToc - 文章目录。
 *
 * 从博客正文（默认 `.article-content`）抽取 h2、h3，生成可点击索引。
 * 点击目录会平滑滚动到对应标题，滚动时高亮当前章节。
 * 正文没有 h2/h3 时隐藏整个模块。
 */
class JxsArticleToc extends JxsComponent {
  requiredRefs = ['nav'];

  #headings = [];
  #usedIds = new Set();
  #scrollRaf = 0;
  #spyRaf = 0;
  #lockActiveId = null;

  connectedCallback() {
    super.connectedCallback();
    this.refs.nav.addEventListener('click', this.#onNavClick);
    window.addEventListener('scroll', this.#onScroll, { passive: true });
    this.#build();
  }

  disconnectedCallback() {
    this.refs.nav?.removeEventListener('click', this.#onNavClick);
    window.removeEventListener('scroll', this.#onScroll);
    if (this.#scrollRaf) cancelAnimationFrame(this.#scrollRaf);
    if (this.#spyRaf) cancelAnimationFrame(this.#spyRaf);
    super.disconnectedCallback();
  }

  #onNavClick = (event) => {
    const link = event.target.closest('a[href^="#"]');
    if (!link || !this.contains(link)) return;

    event.preventDefault();
    event.stopPropagation();

    const id = decodeURIComponent(link.hash.slice(1));
    const heading = document.getElementById(id);
    if (!heading) return;

    this.#lockActiveId = id;
    this.#setActive(id);
    this.#smoothScrollTo(heading);
  };

  #onScroll = () => {
    if (this.#lockActiveId || this.#spyRaf) return;
    this.#spyRaf = requestAnimationFrame(() => {
      this.#spyRaf = 0;
      this.#syncActiveFromScroll();
    });
  };

  #syncActiveFromScroll() {
    if (!this.#headings.length) return;

    const line = this.#headerOffset() + 24;
    let current = this.#headings[0];

    for (const heading of this.#headings) {
      if (heading.getBoundingClientRect().top <= line) {
        current = heading;
      } else {
        break;
      }
    }

    this.#setActive(current.id);
  }

  #smoothScrollTo(heading) {
    const targetY = Math.max(0, heading.getBoundingClientRect().top + window.scrollY - this.#headerOffset() - 12);
    const startY = window.scrollY;
    const distance = targetY - startY;
    if (Math.abs(distance) < 1) {
      this.#lockActiveId = null;
      return;
    }

    const duration = Math.min(900, Math.max(360, Math.abs(distance) * 0.45));
    const startTime = performance.now();
    const easeInOutCubic = (t) => (t < 0.5 ? 4 * t * t * t : 1 - ((-2 * t + 2) ** 3) / 2);

    if (this.#scrollRaf) cancelAnimationFrame(this.#scrollRaf);

    const step = (now) => {
      const progress = Math.min(1, (now - startTime) / duration);
      window.scrollTo(0, startY + distance * easeInOutCubic(progress));
      if (progress < 1) {
        this.#scrollRaf = requestAnimationFrame(step);
      } else {
        this.#scrollRaf = 0;
        this.#lockActiveId = null;
      }
    };

    this.#scrollRaf = requestAnimationFrame(step);
  }

  #build() {
    const article = this.closest('.article-template') || document;
    const selector = this.dataset.contentSelector || '.article-content';
    const content = article.querySelector(selector);
    const headings = content ? [...content.querySelectorAll('h2')] : [];

    if (!headings.length) {
      this.hidden = true;
      return;
    }

    this.hidden = false;
    this.#headings = headings;
    headings.forEach((heading, index) => {
      if (!heading.id) {
        heading.id = this.#uniqueId(this.#slugify(heading.textContent, index));
      }
      heading.style.scrollMarginTop = `${this.#headerOffset() + 12}px`;
    });

    this.refs.nav.replaceChildren(this.#renderList(this.#toTree(headings)));
    this.#syncActiveFromScroll();
  }

  #toTree(headings) {
    const root = [];
    let currentH2 = null;

    headings.forEach((heading) => {
      const item = { heading, children: [] };
      if (heading.tagName === 'H2') {
        currentH2 = item;
        root.push(item);
        return;
      }

      if (currentH2) {
        currentH2.children.push(item);
      } else {
        root.push(item);
      }
    });

    return root;
  }

  #renderList(items) {
    const ul = document.createElement('ul');
    ul.className = 'jxs-article-toc__list';

    items.forEach((item) => {
      const level = item.heading.tagName.toLowerCase();
      const li = document.createElement('li');
      li.className = `jxs-article-toc__item jxs-article-toc__item--${level}`;

      const link = document.createElement('a');
      link.className = 'jxs-article-toc__link';
      link.href = `#${item.heading.id}`;
      link.textContent = item.heading.textContent.trim();
      li.append(link);

      if (item.children.length) {
        li.append(this.#renderList(item.children));
      }

      ul.append(li);
    });

    return ul;
  }

  #setActive(id) {
    this.refs.nav.querySelectorAll('.jxs-article-toc__link').forEach((link) => {
      const isActive = decodeURIComponent(link.hash.slice(1)) === id;
      link.classList.toggle('is-active', isActive);
      if (isActive) {
        link.setAttribute('aria-current', 'location');
      } else {
        link.removeAttribute('aria-current');
      }
    });
  }

  #slugify(text, index) {
    const base = (text || '')
      .trim()
      .toLowerCase()
      .replace(/[^\p{L}\p{N}]+/gu, '-')
      .replace(/^-+|-+$/g, '');
    return base || `article-heading-${index + 1}`;
  }

  #uniqueId(base) {
    let id = base;
    let n = 2;
    while (document.getElementById(id) || this.#usedIds.has(id)) {
      id = `${base}-${n}`;
      n += 1;
    }
    this.#usedIds.add(id);
    return id;
  }

  #headerOffset() {
    const parsed = parseFloat(
      getComputedStyle(document.documentElement).getPropertyValue('--header-height')
    );
    return Number.isFinite(parsed) ? parsed : 80;
  }
}
customElements.define('jxs-article-toc', JxsArticleToc);

/**
 * JxsArticleContent - 文章正文增强。
 *
 * 1. 扫描 `em`，匹配 `Product Card: handle`，拉取产品卡替换所在 `p`。
 * 2. 扫描含 `CTA Button:` 的链接，去掉前缀并加上按钮样式。
 */
class JxsArticleContent extends JxsComponent {
  static #MARKER = /Product\s+Card\s*:\s*([^\s<]+)/i;
  static #CTA_MARKER = /CTA\s+Button\s*:/i;
  static #CTA_PREFIX = /CTA\s+Button\s*:\s*/i;

  connectedCallback() {
    super.connectedCallback();
    this.#hydrateCtaButtons();
    this.#hydrateCards();
  }

  #hydrateCtaButtons() {
    this.querySelectorAll('a').forEach((link) => {
      if (link.classList.contains('jxs-article-cta')) return;
      const text = link.textContent;
      if (!JxsArticleContent.#CTA_MARKER.test(text)) return;

      const label = text.replace(JxsArticleContent.#CTA_PREFIX, '').trim();
      if (!label) return;

      link.textContent = label;
      link.classList.add('jxs-article-cta');
    });
  }

  #hydrateCards() {
    this.querySelectorAll('em').forEach((em) => {
      const match = em.textContent.match(JxsArticleContent.#MARKER);
      if (!match) return;

      const paragraph = em.closest('p');
      if (!paragraph || !this.contains(paragraph) || paragraph.dataset.jxsProductCard) return;

      paragraph.dataset.jxsProductCard = 'pending';
      paragraph.hidden = true;
      this.#renderCard(paragraph, match[1].trim());
    });
  }

  async #renderCard(paragraph, handle) {
    try {
      const data = await this.#fetchProduct(handle);
      if (!this.#isValidCard(data)) return;
      paragraph.replaceWith(this.#createCard(data));
    } catch {
      paragraph.hidden = true;
    }
  }

  async #fetchProduct(handle) {
    const root = window.routes?.root || '';
    const response = await fetch(
      `${root}/products/${encodeURIComponent(handle)}?view=ajax_article_product_card`
    );
    if (!response.ok) throw new Error('Product request failed');
    return this.#parseJson(await response.text());
  }

  #parseJson(text) {
    const trimmed = text.trim();
    try {
      return JSON.parse(trimmed);
    } catch {
      const start = trimmed.indexOf('{');
      const end = trimmed.lastIndexOf('}');
      if (start === -1 || end === -1) throw new Error('Invalid product JSON');
      return JSON.parse(trimmed.slice(start, end + 1));
    }
  }

  #isValidCard(data) {
    return Boolean(data?.available && data.variant_id && data.title && data.url && data.price);
  }

  #decodeText(value) {
    return String(value ?? '').replace(/&amp;/g, '&');
  }

  #createBadges(badges) {
    if (!Array.isArray(badges) || !badges.length) return null;

    const wrap = document.createElement('div');
    wrap.className = 'jxs-article-product-card__badges';

    for (const badge of badges) {
      const text = this.#decodeText(badge?.text).trim();
      if (!text) continue;

      const el = document.createElement('span');
      el.className = 'jxs-article-product-card__badge';
      el.textContent = text;
      if (badge.color) el.style.backgroundColor = badge.color;
      wrap.append(el);
    }

    return wrap.childElementCount ? wrap : null;
  }

  #createCard(data) {
    const root = window.routes?.root || '';
    const checkoutUrl = `${root}/cart/${data.variant_id}:1?checkout`;
    const card = document.createElement('div');
    card.className = 'jxs-article-product-card';

    const media = document.createElement('div');
    media.className = 'jxs-article-product-card__media';
    if (data.image) {
      const mediaLink = document.createElement('a');
      mediaLink.href = data.url;
      const image = document.createElement('img');
      image.src = data.image;
      image.alt = data.image_alt || data.title;
      image.loading = 'lazy';
      image.decoding = 'async';
      mediaLink.append(image);
      media.append(mediaLink);
    }
    card.append(media);

    const body = document.createElement('div');
    body.className = 'jxs-article-product-card__body';

    const badges = this.#createBadges(data.badges);
    if (badges) body.append(badges);

    const title = document.createElement('a');
    title.className = 'jxs-article-product-card__title';
    title.href = data.url;
    title.textContent = data.title;
    body.append(title);

    const price = document.createElement('div');
    price.className = 'jxs-article-product-card__price';
    const sale = document.createElement('span');
    sale.className = 'jxs-article-product-card__price-sale';
    sale.textContent = data.price;
    price.append(sale);
    if (data.compare_at_price) {
      const compare = document.createElement('s');
      compare.className = 'jxs-article-product-card__price-compare';
      compare.textContent = data.compare_at_price;
      price.append(compare);
    }
    if (data.discount_percent > 0) {
      const discount = document.createElement('span');
      discount.className = 'jxs-article-product-card__price-discount';
      discount.textContent = `-${data.discount_percent}%`;
      price.append(discount);
    }
    body.append(price);

    if (data.short_description) {
      const desc = document.createElement('p');
      desc.className = 'jxs-article-product-card__desc';
      desc.textContent = this.#decodeText(data.short_description);
      body.append(desc);
    }

    const actions = document.createElement('div');
    actions.className = 'jxs-article-product-card__actions';

    const learnMore = document.createElement('a');
    learnMore.className = 'jxs-article-product-card__learn';
    learnMore.href = data.url;
    learnMore.textContent = 'Learn More';

    const buyNow = document.createElement('a');
    buyNow.className = 'jxs-article-product-card__buy';
    buyNow.href = checkoutUrl;
    buyNow.textContent = 'Buy Now';
    buyNow.addEventListener('click', (event) => {
      event.preventDefault();
      window.location = checkoutUrl;
    });

    actions.append(learnMore, buyNow);
    body.append(actions);
    card.append(body);
    return card;
  }
}
customElements.define('jxs-article-content', JxsArticleContent);
